import React from 'react'

import Button from 'components_new/atoms/Button'
import Stack from 'components_new/atoms/Stack'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import EntitySlices from 'components_new/organisms/EntitySlices'

import { useTranslation } from 'language/translator'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useSlicesById } from 'redux/hooks/Slices'

export type TConfigureSlice = {
  configured: boolean
  entity_id: string
  id: string
}

interface ConfigureSlicesDialogProps {
  helperText?: string
  /** Called with whether a slice changed between configured and not. */
  onClose: (changed: boolean) => void
  open: boolean
  slices: TConfigureSlice[]
}

/**
 * Configures the given slices in the embedded slice editor of their entities.
 * Admins edit, everyone else reads.
 */
const ConfigureSlicesDialog = (props: ConfigureSlicesDialogProps) => {
  const { helperText, onClose, open, slices } = props

  const { t } = useTranslation()
  const { isAdmin } = useAccountSettings()
  const slicesById = useSlicesById()

  const handleClose = () => {
    onClose(
      slices.some(
        (slice) => slicesById[slice.id]?.disabled === slice.configured
      )
    )
  }

  // One embedded editor per entity, since the slices can span entities.
  const grouped = new Map<string, string[]>()

  slices.forEach((slice) => {
    grouped.set(slice.entity_id, [
      ...(grouped.get(slice.entity_id) ?? []),
      slice.id
    ])
  })

  const byEntity = [...grouped]

  return (
    <SimpleDialog
      open={open}
      onClose={handleClose}
      title={t('entities', 'configure_slices')}
      maxWidth="sm"
      hasClose={true}
      helperText={helperText}
      actions={
        <Button variant="text" onClick={handleClose}>
          {t('global', 'close')}
        </Button>
      }
      sx={{
        zIndex: 1302 // for fullSize
      }}
    >
      <Stack direction="column" sx={{ gap: 1, mt: 1 }}>
        {byEntity.map(([entityId, sliceIds]) => (
          <EntitySlices
            configureSpecificSlices={sliceIds}
            defaultExpanded={true}
            edit={isAdmin}
            id={entityId}
            key={entityId}
          />
        ))}
      </Stack>
    </SimpleDialog>
  )
}

export default ConfigureSlicesDialog
